import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mail, Loader2, ArrowRight } from "lucide-react";
import { CardWatchMark } from "@/components/CardWatchLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/forgot_password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060B19] text-white relative overflow-hidden">
      <div className="pointer-events-none fixed -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#00E676]/[0.08] blur-[140px]" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#1E3A8A]/25 blur-[160px]" />

      <header className="relative z-10 border-b border-white/5">
        <div className="w-full max-w-4xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/signup" className="flex items-center gap-3 group" data-testid="forgot-back-link">
            <ArrowLeft size={16} className="text-slate-500 group-hover:text-white transition-colors" />
            <div className="flex items-center gap-3">
              <CardWatchMark size={30} />
              <span
                className="text-white font-semibold tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16 }}
              >
                Card<span style={{ color: "#00E676" }}>Watch</span>
              </span>
            </div>
          </Link>
        </div>
      </header>

      <main className="relative z-10 max-w-md mx-auto px-6 py-16 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="rounded-3xl border border-white/8 bg-[#0B1226]/70 backdrop-blur-xl p-8"
          data-testid="forgot-card"
        >
          <div className="w-11 h-11 rounded-xl bg-[#00E676]/[0.08] border border-[#00E676]/25 flex items-center justify-center">
            <Mail size={18} className="text-[#00E676]" />
          </div>
          <h1
            className="mt-5 text-white text-3xl font-bold tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="forgot-title"
          >
            Reset your password
          </h1>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            Enter the email you signed up with. If it's on file, we'll send a
            secure reset link valid for 60 minutes.
          </p>

          {sent ? (
            <div
              className="mt-6 rounded-xl border border-[#00E676]/25 bg-[#00E676]/[0.06] px-4 py-4 text-sm text-slate-200"
              data-testid="forgot-sent-confirmation"
            >
              <div
                className="text-[11px] uppercase tracking-[0.22em] text-[#00E676] font-bold"
              >
                Check your inbox
              </div>
              <p className="mt-2 text-slate-300 leading-relaxed">
                If <span className="text-white font-medium">{email}</span> is
                registered, the reset link is on its way. Check your spam folder
                if it doesn't arrive in a couple of minutes.
              </p>
              <button
                onClick={() => navigate("/signup")}
                className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#00E676] hover:underline"
                data-testid="forgot-back-to-signin"
              >
                Back to sign in <ArrowRight size={12} />
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="forgot-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                data-testid="forgot-email-input"
                className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-[#00E676]/60"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="forgot-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#00E676]/40 bg-[#00E676]/[0.08] hover:bg-[#00E676]/[0.16] hover:border-[#00E676] text-[#00E676] font-semibold px-6 py-3 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Send reset link <ArrowRight size={14} /></>}
              </button>
            </form>
          )}

          <div className="mt-6 text-center text-xs text-slate-500">
            Remembered it?{" "}
            <Link to="/signup" className="text-[#00E676] hover:underline" data-testid="forgot-back-signin-link">
              Sign in
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default ForgotPassword;
